import type { RoutineTaskFilters } from '../../../services/routineTaskService';

/**
 * Repeated keys are separate values. A single comma-joined value is the legacy
 * shape (home drill links write `status=PLANNED,ACTIVE,...`). Only use this for
 * fields whose values cannot contain commas. Activity names must not be split.
 */
function parseListParam(
  searchParams: URLSearchParams,
  key: string,
  splitLegacyCommaList: boolean
): string[] {
  const values = searchParams
    .getAll(key)
    .map((v) => v.trim())
    .filter((v) => v.length > 0);
  if (splitLegacyCommaList && values.length === 1 && values[0].includes(',')) {
    return values[0]
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  }
  return values;
}

export function parseRoutineFiltersFromSearchParams(
  searchParams: URLSearchParams
): RoutineTaskFilters {
  const activityNumber = parseListParam(searchParams, 'activityNumber', false);
  // Legacy bookmarks used activityName. Never split it — names contain commas
  // (activity 108205 is "Dead Animals On Travelled Lanes, Shoulders Or Sidewalks").
  // Prefer activityNumber when both are present.
  const activityName =
    activityNumber.length > 0 ? [] : parseListParam(searchParams, 'activityName', false);

  return {
    status: parseListParam(searchParams, 'status', true),
    foremanArea: parseListParam(searchParams, 'foremanArea', false),
    activityNumber,
    activityName,
    priority: searchParams.get('priority') || '',
    startDate: searchParams.get('startDate') || '',
    endDate: searchParams.get('endDate') || '',
    search: searchParams.get('search') || '',
    serviceArea: searchParams.get('serviceArea') || '',
  };
}

/**
 * Write filter values into URLSearchParams (mutates `params`).
 * Array values are appended as repeated keys so commas inside values survive.
 * Writes activityNumber (not activityName) for new URLs.
 */
export function applyRoutineFiltersToSearchParams(
  params: URLSearchParams,
  filters: RoutineTaskFilters
): void {
  // Drop legacy activityName when writing so bookmarks migrate to activityNumber.
  params.delete('activityName');

  Object.entries(filters).forEach(([key, value]) => {
    if (key === 'activityName') {
      // Prefer activityNumber; only write activityName if numbers are absent (legacy apply).
      if (
        (Array.isArray(filters.activityNumber) && filters.activityNumber.length > 0) ||
        (typeof filters.activityNumber === 'string' && filters.activityNumber)
      ) {
        return;
      }
    }

    if (Array.isArray(value)) {
      params.delete(key);
      value.forEach((v) => {
        if (v) params.append(key, String(v));
      });
    } else if (value) {
      params.set(key, value as string);
    } else {
      params.delete(key);
    }
  });
}
